/* FLAT'S ZOOM — the preview scaled, not the frustum narrowed.

   It used to work the other way: the wheel narrowed updateFlatCam's off-axis
   frustum, so the box stayed the size it was and a magnified CROP of the
   deliverable appeared inside it. Everything about that was defensible — the
   render stayed pixel-exact, the box was always the screen — and it read as
   zooming behind a fixed aperture, clipped at the edges. Reported as "the
   zooming on flat output is weird, it is happening inside a mask".

   So Flat now does exactly what On-site does (ui/site-view.js): a CSS
   transform on the element, anchored under the pointer. The whole deliverable
   grows as one object and nothing is cut off.

   Two things fall out of the markup for free, because #flatWatermark and
   #fitboxMaskSvg are CHILDREN of #fitbox: both scale with the render, so the
   watermark no longer needs to compensate for a crop it can no longer be
   inside of, and the mask overlay stays put on the content it traces.

   NOT the camera's business any more. updateScreenCam still accepts a zoom and
   still applies S.flatPan — deliberately untouched, because tools/verify.mjs's
   section 36 holds that arithmetic bit-exact against every delivered export
   and a one-ulp drift there would change real pixels. Nothing passes a zoom
   above 1 any more, so those branches are simply never taken.

   View state, never the document's: like siteZoom, this is how you are looking
   at the piece rather than anything about the piece (see state/store.js). */

import { requestRender } from '../state/bus.js';
import { $, S, clamp } from '../state/store.js';

export const FLAT_ZOOM_MIN = 0.5, FLAT_ZOOM_MAX = 8;

/* WHICH ELEMENT IS "FLAT" RIGHT NOW. Standalone Flat is #fitbox; in Split the
   right half is its own Flat pane, #fitboxSplitR — unless a site photo has taken
   that half for the mockup, which has a zoom of its own (ui/site-view.js). A
   tall screen sits small in its half of Split, which is why that half needs
   the zoom as much as standalone Flat does. */
export function flatTarget(){
  if(S.view === 'flat') return $('#fitbox');
  if(S.view === 'split' && !S.photo) return $('#fitboxSplitR');
  return null;
}
export const flatZoomable = () => !!flatTarget();

/* How far the render may be dragged. Half its own scaled size, so the picture
   can always be pulled far enough for any corner to reach the middle of the
   stage, and never so far that it leaves the stage empty. */
function clampFlatPan(){
  const fb = flatTarget();
  if(!fb) return;
  const z = S.flatZoom || 1;
  const mx = Math.max(0, fb.clientWidth * z / 2);
  const my = Math.max(0, fb.clientHeight * z / 2);
  S.flatPan.x = clamp(S.flatPan.x, -mx, mx);
  S.flatPan.y = clamp(S.flatPan.y, -my, my);
}

/** Write the current zoom/pan onto #fitbox, and update the readout. */
export function applyFlatTransform(){
  const fb = flatTarget();
  /* #fitbox is shared — setView reparents this same element into Split's left
     pane, and Orbit renders through it too. A transform left on it would
     follow it there, so whichever of the two is not Flat right now is cleared. */
  for(const other of [$('#fitbox'), $('#fitboxSplitR')])
    if(other && other !== fb && other.style.transform) other.style.transform = '';
  if(!fb) return;
  const z = S.flatZoom || 1, p = S.flatPan || { x: 0, y: 0 };
  fb.style.transformOrigin = 'center center';
  fb.style.transform = `translate(${p.x}px, ${p.y}px) scale(${z})`;
  const zi = $('#zoomVal');
  if(zi) zi.textContent = Math.round(z * 100) + '%';
}

/**
 * @param z       the zoom to go to
 * @param anchor  {x,y} in client coords — the point to keep still, normally
 *                the pointer. Omitted, the centre stays put.
 */
export function setFlatZoom(z, anchor){
  const z0 = S.flatZoom || 1;
  const z1 = clamp(z, FLAT_ZOOM_MIN, FLAT_ZOOM_MAX);
  if(z1 === z0){
    // asking for 100% when already there is a request to recentre
    if(z1 === 1) S.flatPan = { x: 0, y: 0 };
    applyFlatTransform();
    return;
  }
  const fb = flatTarget();
  if(fb && anchor){
    /* Keep whatever sits under the cursor pinned while the scale changes —
       the same arithmetic setSiteZoom uses, and the half of this the old
       frustum zoom never had: it always magnified about the screen's centre,
       so the thing you were pointing at slid away as you zoomed toward it. */
    const r = fb.getBoundingClientRect();
    const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    const p = S.flatPan;
    const ux = anchor.x - cx + p.x, uy = anchor.y - cy + p.y;
    S.flatPan = { x: ux - (z1 / z0) * (ux - p.x), y: uy - (z1 / z0) * (uy - p.y) };
  }
  S.flatZoom = z1;
  if(z1 === 1) S.flatPan = { x: 0, y: 0 };
  else clampFlatPan();
  applyFlatTransform();
  requestRender();
}

/** @param dxPx,dyPx pointer movement in CSS pixels. */
export function panFlatByPixels(dxPx, dyPx){
  if(!S.flatPan) S.flatPan = { x: 0, y: 0 };
  S.flatPan.x += dxPx;
  S.flatPan.y += dyPx;
  clampFlatPan();
  applyFlatTransform();
  requestRender();
}

/** Back to the whole deliverable, centred — what the Fit control presses. */
export function flatFit(){
  S.flatZoom = 1;
  S.flatPan = { x: 0, y: 0 };
  applyFlatTransform();
  requestRender();
}
